import createModule from "../createModule"
import view from "./view"
import { getBasePositionSetWithSegment, getBaseSignature } from "selectors/base"
import { getViewType, getSelectedModelId, getShowNotHeld, getIsExpanded, getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { getPerformanceFetchStartDate } from "selectors/dates"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getSortedContributors, getSelectedGroupValue, getAvailableRollups, getSortedGroupContributors, getContributorsSummaryText, getCsvData, getGroupBy, getIncludeNotHeld, getSelectedGroupSiblings, getSelectedGroup } from "./selectors"
import { getGroupsDropdownColumns } from "./selectors/group"
import { getSegment } from "modules/analyze-header/selectors/segment"
import { classificationsQuery, generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { getIsOpPro } from "modules/account-root/selectors"
import { getQueryGroupBy } from "helpers/rollups"
import { VIEW_TYPES } from "helpers/base"
import { getIsRfrAttributionEnabled } from "selectors/features"
import { getResidualReturnSubquery } from "helpers/residualAttribution"

export default createModule({
  view,
  externalProps: (state, props) => ({
    modelId: getSelectedModelId(state, props),
    portfolioId: state.global.selected.portfolio,
    account: state.global.selected.account,
    base: getBasePositionSetWithSegment(state, props),
    baseSignature: getBaseSignature(state, props),
    fetchStartDate: getPerformanceFetchStartDate(state, props),
    viewType: getViewType(state, props),
    segment: getSegment(state, props),
    classifications: getClassifications(state, props),
    includeNotHeld: getIncludeNotHeld(state, props),
    expand: getIsExpanded(state, props),
    currency: getCurrency(state, props),
    isRfrAttributionEnabled: getIsRfrAttributionEnabled(state),
  }),
  required: ["account", "portfolioId"],
  data: [{
    resource: "performanceContributors",
    as: null,
    url: ({ modelId, portfolioId, selectedDate, viewType, expand, base, baseSignature, fetchStartDate, segment, classifications, includeNotHeld, currency, isRfrAttributionEnabled }) => {
      return {
        id: `performanceContributors-${modelId}${portfolioId}${fetchStartDate}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}${isRfrAttributionEnabled}`,
        query: `query ($modelId: String!, $portfolioId: String!, $from: Date!, $to: Date!, $expand: Boolean, $base: [PositionSetInput], $segment: Segment, $includeNotHeld: Boolean) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $from, to: $to, base: $base, interval: AUTO) {
                performanceContributors(includeNotHeld: $includeNotHeld) {
                  id
                  modelProviderId
                  assetClass
                  assetSubclass
                  averagePercentEquity
                  totalReturn {
                    cumulativePercent
                    cumulativeCurrency
                  }
                  country
                  currency
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  notHeld
                  description
                  isUnmodeled
                  bloombergTicker
                  attribution {
                    summary {
                      factorsReturn {
                        cumulativePercent
                        cumulativeCurrency
                      }
                      specificReturn {
                        cumulativePercent
                        cumulativeCurrency
                      }
                      tradingReturn {
                        cumulativePercent
                        cumulativeCurrency
                      }
                      ${isRfrAttributionEnabled ? getResidualReturnSubquery(true) : ""}
                    }
                    factors {
                      id
                      category
                      return {
                        cumulativePercent
                        cumulativeCurrency
                      }
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          portfolioId,
          from: fetchStartDate,
          to: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
        },
      }
    },
  }, {
    resource: "groupedPerformanceContributors",
    as: null,
    url: ({ portfolioId, modelId, viewType, expand, selectedDate, contributorsGrouping, fetchStartDate, baseSignature, base, segment, classifications, includeNotHeld, currency, isRfrAttributionEnabled }) => {
      if (!contributorsGrouping) {
        return null
      }

      const queryGroupBy = getQueryGroupBy(contributorsGrouping, viewType === VIEW_TYPES.ACTIVE)

      return {
        id: `groupedPerformanceContributors-${modelId}${portfolioId}${fetchStartDate}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(queryGroupBy)}${JSON.stringify(segment)}${includeNotHeld}${currency}${isRfrAttributionEnabled}`,
        query: `query ($modelId: String!, $portfolioId: String!, $from: Date!, $to: Date!, $expand: Boolean, $base: [PositionSetInput], $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $includeNotHeld: Boolean) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $from, to: $to, base: $base, interval: AUTO) {
                groupedPerformanceContributors (groupBy: $groupBy, groupById: $groupById, classificationTier: $tier, includeNotHeld: $includeNotHeld) {
                  name
                  id
                  totalReturn {
                    cumulativePercent
                    cumulativeCurrency
                  }
                  averagePercentEquity
                  attribution {
                    summary {
                      factorsReturn {
                        cumulativePercent
                        cumulativeCurrency
                      }
                      specificReturn {
                        cumulativePercent
                        cumulativeCurrency
                      }
                      tradingReturn {
                        cumulativePercent
                        cumulativeCurrency
                      }
                      ${isRfrAttributionEnabled ? getResidualReturnSubquery(true) : ""}
                    }
                    factors {
                      id
                      category
                      return {
                        cumulativePercent
                        cumulativeCurrency
                      }
                    }
                  }
                  contributors {
                    id
                    assetClass
                    country
                    notHeld
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    currency
                    description
                    isUnmodeled
                    averagePercentEquity
                    totalReturn {
                      cumulativePercent
                      cumulativeCurrency
                    }
                    bloombergTicker
                    modelProviderId
                    attribution {
                      summary {
                        factorsReturn {
                          cumulativePercent
                          cumulativeCurrency
                        }
                        specificReturn {
                          cumulativePercent
                          cumulativeCurrency
                        }
                        tradingReturn {
                          cumulativePercent
                          cumulativeCurrency
                        }
                        ${isRfrAttributionEnabled ? getResidualReturnSubquery(true) : ""}
                      }
                      factors {
                        id
                        category
                        return {
                          cumulativePercent
                          cumulativeCurrency
                        }
                      }
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          portfolioId,
          from: fetchStartDate,
          to: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          ...queryGroupBy,
        },
      }
    },
  }, {
    resource: "classifications",
    as: null,
    url: () => {
      return {
        id: "classifications",
        query: classificationsQuery,
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    data: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    csvData: getCsvData(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    isOpPro: getIsOpPro(state, props),
    showNotHeld: getShowNotHeld(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    selectedGroup: getSelectedGroup(state, props),
    groupBy: getGroupBy(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
  }),
  actions: {
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
